import type React from "react";

/**
Shared layout primitives for every top-level page, so the in-page header never scrolls out of
view and tables always scroll internally instead of the whole page - one visual language instead
of each page inventing its own. Every page follows the same three-part structure:

```tsx
<div style={pageShellStyle}>
  <div style={pageHeaderRegionStyle}>
    <div style={pageHeaderStyle}>
      <h1 style={pageTitleStyle}>...</h1>
      {actions}
    </div>
  </div>
  <div style={pageScrollableContentStyle}> or <div style={pageFillContentStyle}>
    ...
  </div>
</div>
```

`pageScrollableContentStyle` is for form/detail pages (membership lists, tabs, cards) where the
content itself decides its height and the whole region scrolls once it overflows.
`pageFillContentStyle` is for table/grid pages: it never scrolls itself, so the `<Grid>` inside it
(given `style={{ flex: "1 1 auto", minHeight: 0 }}`) owns the actual scrollbar instead - only the
table body scrolls while the header/search/actions above it stay fixed.
*/
export const pageShellStyle: React.CSSProperties = { height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" };

export const pageHeaderRegionStyle: React.CSSProperties = { flex: "0 0 auto", padding: "24px 24px 0" };

export const pageScrollableContentStyle: React.CSSProperties = { flex: "1 1 auto", minHeight: 0, overflow: "auto", padding: 24, display: "grid", gap: 20, alignContent: "start" };

export const pageFillContentStyle: React.CSSProperties = { flex: "1 1 auto", minHeight: 0, padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 16, overflow: "hidden" };

export const pageHeaderStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" };

export const pageTitleStyle: React.CSSProperties = { margin: 0, fontSize: 24, fontWeight: 700, color: "#172033" };

export const cardStyle: React.CSSProperties = {
  borderRadius: 0,
  border: "1px solid #dbe2ea",
  boxShadow: "0 20px 45px rgba(15, 23, 42, 0.07)",
  overflow: "hidden",
};

/**
`cardStyle` variant for a Card that must fill `pageFillContentStyle`'s remaining height and hand
its own vertical space down to an inner `<Grid>`, instead of growing to its natural content height.
*/
export const fillCardStyle: React.CSSProperties = { ...cardStyle, flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" };

/**
Every section title within a card - one size everywhere, deliberately smaller than `pageTitleStyle`.
*/
export const sectionTitleStyle: React.CSSProperties = { margin: 0, fontSize: 16, fontWeight: 700, color: "#172033" };

export const mutedTextStyle: React.CSSProperties = { margin: 0, color: "#526077", fontSize: 15, lineHeight: 1.5 };

export const rowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 14px",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  gap: 12,
};

/**
One actions column per table row, holding every icon button with the same gap - instead of one
narrow `GridColumn` per button, whose spacing then depends on each column's own (inconsistently
chosen) width.
*/
export const tableRowActionsStyle: React.CSSProperties = { display: "flex", gap: 8 };
